// Notifications JavaScript
// Handles notification settings, status checking, and UI interactions

class NotificationManager {
  constructor() {
    this.statusElement = null;
    this.actionsElement = null;
    this.debugElement = null;
    this.isBrave = this.detectBrave();
    this.init();
  }

  init() {
    const initialize = () => {
      this.statusElement = document.getElementById('notification-status');
      this.actionsElement = document.getElementById('notification-actions');
      this.debugElement = document.getElementById('debug-info');

      if (this.statusElement) {
        this.checkNotificationStatus();
        this.setupEventListeners();
        this.showBraveWarningIfNeeded();
      }

      // Make globally accessible
      window.notificationControls = {
        checkStatus: this.checkNotificationStatus.bind(this),
        enableNotifications: this.enableNotifications.bind(this),
        disableNotifications: this.disableNotifications.bind(this),
        testNotification: this.testNotification.bind(this),
        showManualPrompt: this.showManualPrompt.bind(this)
      };
    };

    // Listen to both DOMContentLoaded and Turbo events
    document.addEventListener('DOMContentLoaded', initialize);
    document.addEventListener('turbo:load', initialize);
    document.addEventListener('turbo:render', initialize);
  }

  async checkNotificationStatus() {
    if (!this.statusElement) return;

    try {
      const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      const permission = isSupported ? Notification.permission : 'unsupported';

      let hasToken = false;
      if (window.pushNotifications && window.pushNotifications.currentToken) {
        hasToken = true;
      }

      this.updateNotificationUI(permission, hasToken, isSupported);

      // Check server-side status
      try {
        const response = await fetch('/api/fcm_tokens/status', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.updateDebugInfo(data);
          hasToken = data.has_token;
          this.updateNotificationUI(permission, hasToken, isSupported);
        }
      } catch (error) {
        console.warn('Could not check server-side notification status:', error);
      }
    } catch (error) {
      console.error('Error in checkNotificationStatus:', error);
      this.showNotificationError('Error checking notification status');
    }
  }

  updateNotificationUI(permission, hasToken, isSupported) {
    if (!this.statusElement || !this.actionsElement) return;

    this.statusElement.innerHTML = this.getStatusHTML(permission, hasToken, isSupported);
    this.actionsElement.innerHTML = this.getActionsHTML(permission, hasToken, isSupported);
    this.actionsElement.style.display = 'block';
  }

  getStatusHTML(permission, hasToken, isSupported) {
    if (!isSupported) {
      return `
        <div class="notification-status--unsupported">
          <strong>❌ Push notifications not supported</strong>
          <p>Your browser or device doesn't support push notifications.</p>
        </div>
      `;
    }

    if (permission === 'granted' && hasToken) {
      return `
        <div class="notification-status--enabled">
          <strong>✅ Push notifications enabled</strong>
          <p>You'll receive notifications even when the app is closed.</p>
        </div>
      `;
    }

    if (permission === 'denied') {
      return `
        <div class="notification-status--disabled">
          <strong>🚫 Push notifications blocked</strong>
          <p>You've blocked notifications. Enable them in your browser settings.</p>
        </div>
      `;
    }

    return `
      <div class="notification-status--disabled">
        <strong>⚠️ Push notifications disabled</strong>
        <p>Enable notifications to receive reminders when the app is closed.</p>
      </div>
    `;
  }

  getActionsHTML(permission, hasToken, isSupported) {
    if (!isSupported) {
      return `
        <p>Consider using a modern browser like Chrome, Firefox, or Safari for push notification support.</p>
      `;
    }

    if (permission === 'granted' && hasToken) {
      return `
        <button class="btn-inline btn--md btn-inline--secondary" onclick="notificationControls.testNotification()">
          🧪 Send Test Notification
        </button>
        <button class="btn-inline btn--md btn-inline--danger" onclick="notificationControls.disableNotifications()" id="disable-notifications-btn">
          🔇 Disable Notifications
        </button>
      `;
    }

    if (permission === 'denied') {
      return `
        <div>
          <p><strong>To enable notifications:</strong></p>
          <ol style="text-align: left; margin: 0.5rem 0; padding-left: 1.5rem;">
            <li>Click the 🔒 or ℹ️ icon in your address bar</li>
            <li>Find "Notifications" and change to "Allow"</li>
            <li>Refresh this page</li>
          </ol>
          <button class="btn-inline btn--md btn-inline--secondary" onclick="window.location.reload()">
            🔄 Refresh Page
          </button>
        </div>
      `;
    }

    return `
      <button class="btn-inline btn--md btn-inline--primary" onclick="notificationControls.enableNotifications()">
        🔔 Enable Push Notifications
      </button>
    `;
  }

  async enableNotifications() {
    try {
      if (window.pushNotifications) {
        await window.pushNotifications.requestPermission();
        setTimeout(() => this.checkNotificationStatus(), 1000);
      } else {
        // Fallback to direct browser API
        const permission = await Notification.requestPermission();
        setTimeout(() => this.checkNotificationStatus(), 1000);
        if (permission === 'granted') {
          this.showNotificationToast('✅ Notifications enabled!', 'success');
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      this.showNotificationError('Failed to enable notifications');
    }
  }

  async disableNotifications() {
    const disableBtn = document.getElementById('disable-notifications-btn');
    const originalText = disableBtn ? disableBtn.innerHTML : '';
    
    try {
      console.log('Attempting to disable notifications...');
      
      // Show loading state
      if (disableBtn) {
        disableBtn.innerHTML = '⏳ Disabling...';
        disableBtn.disabled = true;
      }
      
      if (!window.pushNotifications) {
        console.error('PushNotifications not available');
        this.showNotificationError('Push notifications service not available');
        return;
      }

      console.log('Calling unsubscribe...');
      await window.pushNotifications.unsubscribe();
      
      console.log('Unsubscribe completed, refreshing status...');
      this.showNotificationToast('🔇 Notifications disabled successfully', 'success');
      
      // Refresh status after a delay
      setTimeout(() => {
        this.checkNotificationStatus();
      }, 1000);
      
    } catch (error) {
      console.error('Error disabling notifications:', error);
      this.showNotificationError(`Failed to disable notifications: ${error.message}`);
      
      // Restore button state on error
      if (disableBtn) {
        disableBtn.innerHTML = originalText;
        disableBtn.disabled = false;
      }
    }
  }

  async testNotification() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    try {
      const response = await fetch('/notifications/send_test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          title: 'Test Notification',
          body: 'This is a test push notification from your notification settings!'
        })
      });

      const data = await response.json();
      if (data.success) {
        this.showNotificationToast('✅ Test notification sent! Check your device.', 'success');
      } else {
        this.showNotificationToast('❌ Failed to send test notification', 'error');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      this.showNotificationToast('❌ Error sending test notification', 'error');
    }
  }

  showManualPrompt() {
    if (window.pushNotifications && window.pushNotifications.showManualPrompt) {
      window.pushNotifications.showManualPrompt();
    } else {
      this.enableNotifications();
    }
  }

  showNotificationError(message) {
    if (this.statusElement) {
      this.statusElement.innerHTML = `
        <div class="notification-status--disabled">
          <strong>❌ Error</strong>
          <p>${message}</p>
        </div>
      `;
    }
  }

  showNotificationToast(message, type = 'info') {
    // Remove existing toast
    const existingToast = document.getElementById('notification-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.id = 'notification-toast';
    toast.innerHTML = `<div class="notification-toast notification-toast--${type}">${message}</div>`;

    document.body.appendChild(toast);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.style.animation = 'slideInUp 0.3s ease-in reverse';
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  updateDebugInfo(data) {
    if (this.debugElement) {
      this.debugElement.textContent = JSON.stringify(data, null, 2);
    }
  }

  setupEventListeners() {
    // Listen for push notification events
    window.addEventListener('push-notification-received', (event) => {
      console.log('Push notification received:', event.detail);
      // You could show a toast or update UI here
    });

  }



  detectBrave() {
    return (navigator.brave && navigator.brave.isBrave) || false;
  }

  showBraveWarningIfNeeded() {
    // Only show once per browser, even across page reloads
    const BRAVE_SHOWN_KEY = 'braveWarningShownOnce';
    if (localStorage.getItem(BRAVE_SHOWN_KEY)) {
      return;
    }
    
    // Only show if user has created reminders
    const mainElement = document.querySelector('main[data-notifications-has-reminders-value]');
    const hasReminders = mainElement ? mainElement.dataset.notificationsHasRemindersValue === 'true' : false;
    
    if (this.isBrave && hasReminders && Notification.permission === 'default') {
      const warningElement = document.getElementById('brave-browser-warning');
      if (warningElement) {
        warningElement.style.display = 'block';
        // Mark as shown so we never show it again
        localStorage.setItem(BRAVE_SHOWN_KEY, 'true');
      }
    }
  }

  hideBraveWarning() {
    const warningElement = document.getElementById('brave-browser-warning');
    if (warningElement) {
      warningElement.style.display = 'none';
    }
  }
}

// Initialize the notification manager
const notificationManager = new NotificationManager();

// Device Management Functions
window.loadDevices = async function() {
  const deviceList = document.getElementById('device-list');
  if (!deviceList) return;

  try {
    const response = await fetch('/api/fcm_tokens', {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to load devices');
    }

    const data = await response.json();
    renderDeviceList(data.devices);
  } catch (error) {
    console.error('Error loading devices:', error);
    deviceList.innerHTML = `
      <div class="device-list__error">
        <span>❌ Failed to load devices. Please refresh the page.</span>
      </div>
    `;
  }
};

function renderDeviceList(devices) {
  const deviceList = document.getElementById('device-list');
  if (!deviceList) return;

  if (!devices || devices.length === 0) {
    deviceList.innerHTML = `
      <div class="device-list__empty">
        <div class="device-list__empty-icon">📱</div>
        <div class="device-list__empty-text">
          <p>No devices registered yet</p>
          <p class="device-list__empty-hint">Enable push notifications above to register this device</p>
        </div>
      </div>
    `;
    return;
  }

  deviceList.innerHTML = devices.map(device => `
    <div class="device-item ${device.enabled ? '' : 'device-item--disabled'}" data-device-id="${device.id}">
      <div class="device-item__icon">${device.platform_icon}</div>
      <div class="device-item__info">
        <div class="device-item__name">${escapeHtml(device.device_name)}</div>
        <div class="device-item__meta">
          <span class="device-item__platform">${escapeHtml(device.platform)}</span>
          ${device.last_used_at ? 
            `<span class="device-item__last-used">Last used ${formatRelativeTime(device.last_used_at)}</span>` : 
            '<span class="device-item__last-used">Never used</span>'
          }
          ${device.stale ? '<span class="device-item__stale-badge">Stale</span>' : ''}
        </div>
      </div>
      <div class="device-item__actions">
        <label class="device-item__toggle">
          <input type="checkbox" 
                 ${device.enabled ? 'checked' : ''} 
                 onchange="toggleDevice(${device.id}, this.checked)"
                 class="device-toggle-switch">
          <span class="device-toggle-label">${device.enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
        <button class="device-item__delete" onclick="deleteDevice(${device.id})" title="Remove device">
          🗑️
        </button>
      </div>
    </div>
  `).join('');
}

window.toggleDevice = async function(deviceId, enabled) {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    
    const response = await fetch(`/api/fcm_tokens/${deviceId}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ enabled })
    });

    if (!response.ok) {
      throw new Error('Failed to update device');
    }

    const data = await response.json();
    console.log('Device updated:', data);
    
    // Reload device list
    await loadDevices();
  } catch (error) {
    console.error('Error toggling device:', error);
    alert('Failed to update device. Please try again.');
    // Reload to reset UI
    await loadDevices();
  }
};

window.deleteDevice = async function(deviceId) {
  if (!confirm('Are you sure you want to remove this device? It will no longer receive notifications.')) {
    return;
  }

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    
    const response = await fetch(`/api/fcm_tokens/${deviceId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': csrfToken,
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to delete device');
    }

    console.log('Device deleted successfully');
    
    // Reload device list
    await loadDevices();
  } catch (error) {
    console.error('Error deleting device:', error);
    alert('Failed to remove device. Please try again.');
  }
};

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Load devices when page loads if on settings tab
// Use both DOMContentLoaded (initial load) and turbo:load (Turbo navigation)
function initializeDeviceList() {
  const deviceList = document.getElementById('device-list');
  if (deviceList) {
    loadDevices();
  }
}

document.addEventListener('DOMContentLoaded', initializeDeviceList);
document.addEventListener('turbo:load', initializeDeviceList);
document.addEventListener('turbo:render', initializeDeviceList);

// Export for use in other modules
export default NotificationManager;